import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Divider,
  Alert,
  Chip,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Button from '@mui/material/Button';
import { useApi } from '../contexts/Api';
import ProductSkuPicker from './ProductSkuPicker';

export const buildPayload = (formData, tiers) => {
  const cleanedTiers = tiers
    .filter(t => t.min_qty !== '' && t.discount_pct !== '')
    .map(t => ({
      min_qty: parseInt(t.min_qty, 10),
      max_qty: t.max_qty === '' ? null : parseInt(t.max_qty, 10),
      discount_pct: parseFloat(t.discount_pct)
    }))
    .sort((a, b) => a.min_qty - b.min_qty);

  return {
    name: formData.name,
    active: formData.active ? 1 : 0,
    cat_ids:    Array.isArray(formData.cat_ids)    && formData.cat_ids.length    ? formData.cat_ids.map(Number)    : null,
    parent_ids: Array.isArray(formData.parent_ids) && formData.parent_ids.length ? formData.parent_ids.map(Number) : null,
    marka_ids:  Array.isArray(formData.marka_ids)  && formData.marka_ids.length  ? formData.marka_ids.map(Number)  : null,
    min_price: formData.min_price === '' ? null : parseFloat(formData.min_price),
    max_price: formData.max_price === '' ? null : parseFloat(formData.max_price),
    min_weight: formData.min_weight === '' ? null : parseFloat(formData.min_weight),
    max_weight: formData.max_weight === '' ? null : parseFloat(formData.max_weight),
    sku_list: formData.sku_list ? formData.sku_list.split(/[\s,]+/).filter(Boolean).join(',') : null,
    tiers: cleanedTiers
  };
};

export const validateForm = (formData, tiers) => {
  if (!formData.name?.trim()) return 'Rule name is required';
  const cleaned = tiers.filter(t => t.min_qty !== '' && t.discount_pct !== '');
  if (!cleaned.length) return 'At least one valid tier is required';
  for (const t of cleaned) {
    const minQ = parseInt(t.min_qty, 10);
    if (isNaN(minQ) || minQ < 1) return 'Tier min qty must be ≥ 1';
    if (t.max_qty !== '' && parseInt(t.max_qty, 10) < minQ) return 'Tier max qty must be ≥ min qty';
    const pct = parseFloat(t.discount_pct);
    if (isNaN(pct) || pct < 0 || pct > 100) return 'Tier discount % must be between 0 and 100';
  }
  return null;
};

const VolumeDiscountForm = ({ initialData, onChange }) => {
  const { categories, brands, coefficientRules } = useApi();

  const [formData, setFormData] = useState({
    name: '',
    active: true,
    cat_ids: [],
    parent_ids: [],
    marka_ids: [],
    min_price: '',
    max_price: '',
    min_weight: '',
    max_weight: '',
    sku_list: ''
  });
  const [tiers, setTiers] = useState([
    { min_qty: '1', max_qty: '3', discount_pct: '0' },
    { min_qty: '4', max_qty: '10', discount_pct: '10' },
    { min_qty: '11', max_qty: '', discount_pct: '20' }
  ]);

  // Split categories into parents (parent_id === '0') and children
  const parentCategories = useMemo(
    () => (categories || []).filter(c => String(c.parent_id) === '0'),
    [categories]
  );
  const childCategories = useMemo(
    () => (categories || []).filter(c => String(c.parent_id) !== '0'),
    [categories]
  );
  const sortedBrands = useMemo(
    () => [...(brands || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [brands]
  );

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        active: initialData.active == 1,
        cat_ids:    Array.isArray(initialData.cat_ids)    ? initialData.cat_ids.map(Number)    : (initialData.cat_id    ? [Number(initialData.cat_id)]    : []),
        parent_ids: Array.isArray(initialData.parent_ids) ? initialData.parent_ids.map(Number) : (initialData.parent_id ? [Number(initialData.parent_id)] : []),
        marka_ids:  Array.isArray(initialData.marka_ids)  ? initialData.marka_ids.map(Number)  : (initialData.marka_id  ? [Number(initialData.marka_id)]  : []),
        min_price: initialData.min_price ?? '',
        max_price: initialData.max_price ?? '',
        min_weight: initialData.min_weight ?? '',
        max_weight: initialData.max_weight ?? '',
        sku_list: initialData.sku_list ?? ''
      });
      let t = initialData.tiers;
      if (typeof t === 'string') {
        try { t = JSON.parse(t); } catch { t = []; }
      }
      if (Array.isArray(t) && t.length) {
        setTiers(t.map(x => ({
          min_qty: String(x.min_qty ?? ''),
          max_qty: x.max_qty == null ? '' : String(x.max_qty),
          discount_pct: String(x.discount_pct ?? '')
        })));
      }
    }
  }, [initialData]);

  useEffect(() => {
    onChange?.({ formData, tiers });
  }, [formData, tiers]);

  const setField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateTier = (i, field, value) => {
    const next = [...tiers];
    next[i] = { ...next[i], [field]: value };
    setTiers(next);
  };

  const addTier = () => {
    const last = tiers[tiers.length - 1];
    const nextMin = last?.max_qty ? String(parseInt(last.max_qty, 10) + 1) : '';
    setTiers([...tiers, { min_qty: nextMin, max_qty: '', discount_pct: '' }]);
  };

  const removeTier = (i) => {
    if (tiers.length <= 1) return;
    setTiers(tiers.filter((_, idx) => idx !== i));
  };

  // Resolve selected IDs into option objects for Autocomplete
  const selectedParents = parentCategories.filter(c => formData.parent_ids.includes(Number(c.id)));
  const selectedChildCats = childCategories.filter(c => formData.cat_ids.includes(Number(c.id)));
  const selectedBrands = sortedBrands.filter(b => formData.marka_ids.includes(Number(b.id)));

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
        Basic Info
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <TextField
            label="Rule Name"
            value={formData.name}
            onChange={(e) => setField('name', e.target.value)}
            fullWidth
            required
            placeholder="e.g. Bulk discount for cable accessories"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={(e) => setField('active', e.target.checked)}
                />
              }
              label={formData.active ? 'Active' : 'Inactive'}
            />
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
        Filters <Chip label="Leave empty for all" size="small" sx={{ ml: 1, fontSize: '10px', height: '18px' }} />
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Rule applies to products that match ALL non-empty filters below. Multi-select fields use OR within the field.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            multiple
            options={parentCategories}
            getOptionLabel={(opt) => opt.name_en || `#${opt.id}`}
            isOptionEqualToValue={(opt, val) => Number(opt.id) === Number(val.id)}
            value={selectedParents}
            onChange={(_, val) => setField('parent_ids', val.map(v => Number(v.id)))}
            renderInput={(params) => (
              <TextField {...params} label="Parent Categories" placeholder="Select one or more..." />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={option.name_en || `#${option.id}`}
                  {...getTagProps({ index })}
                  sx={{ backgroundColor: '#dbeafe', color: '#1e40af' }}
                />
              ))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            multiple
            options={childCategories}
            getOptionLabel={(opt) => opt.name_en || `#${opt.id}`}
            isOptionEqualToValue={(opt, val) => Number(opt.id) === Number(val.id)}
            value={selectedChildCats}
            onChange={(_, val) => setField('cat_ids', val.map(v => Number(v.id)))}
            renderInput={(params) => (
              <TextField {...params} label="Categories" placeholder="Select one or more..." />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={option.name_en || `#${option.id}`}
                  {...getTagProps({ index })}
                  sx={{ backgroundColor: '#dcfce7', color: '#166534' }}
                />
              ))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={sortedBrands}
            getOptionLabel={(opt) => opt.name || `#${opt.id}`}
            isOptionEqualToValue={(opt, val) => Number(opt.id) === Number(val.id)}
            value={selectedBrands}
            onChange={(_, val) => setField('marka_ids', val.map(v => Number(v.id)))}
            renderInput={(params) => (
              <TextField {...params} label="Brands" placeholder="Select one or more..." />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={option.name || `#${option.id}`}
                  {...getTagProps({ index })}
                  sx={{ backgroundColor: '#ffedd5', color: '#9a3412' }}
                />
              ))
            }
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <TextField
            label="Min Price (€)"
            type="number"
            value={formData.min_price}
            onChange={(e) => setField('min_price', e.target.value)}
            fullWidth
            inputProps={{ step: '0.01' }}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Max Price (€)"
            type="number"
            value={formData.max_price}
            onChange={(e) => setField('max_price', e.target.value)}
            fullWidth
            inputProps={{ step: '0.01' }}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Min Weight (kg)"
            type="number"
            value={formData.min_weight}
            onChange={(e) => setField('min_weight', e.target.value)}
            fullWidth
            inputProps={{ step: '0.001' }}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Max Weight (kg)"
            type="number"
            value={formData.max_weight}
            onChange={(e) => setField('max_weight', e.target.value)}
            fullWidth
            inputProps={{ step: '0.001' }}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 2, backgroundColor: '#fbfbfd' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
              Specific Products (SKU)
            </Typography>
            <ProductSkuPicker
              value={formData.sku_list}
              onChange={(v) => setField('sku_list', v)}
            />
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
          Discount Tiers
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addTier}
          size="small"
          sx={{ borderColor: '#845adf', color: '#845adf', '&:hover': { borderColor: '#6c48c5', backgroundColor: '#f3f0ff' } }}
        >
          Add Tier
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Customers buying within each quantity range get the listed discount. Use empty "Max Qty" for the last open-ended tier (e.g. 11+).
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {tiers.map((tier, i) => (
          <Box
            key={i}
            sx={{
              display: 'grid',
              gridTemplateColumns: '40px 1fr 1fr 1fr 40px',
              gap: 1.5,
              alignItems: 'center',
              p: 1.5,
              border: '1px solid #e5e7eb',
              borderRadius: 1,
              backgroundColor: '#fafafa'
            }}
          >
            <Chip label={`#${i + 1}`} size="small" sx={{ backgroundColor: '#845adf', color: 'white', fontWeight: 600 }} />
            <TextField
              label="Min Qty"
              type="number"
              size="small"
              value={tier.min_qty}
              onChange={(e) => updateTier(i, 'min_qty', e.target.value)}
              inputProps={{ min: 1 }}
              required
            />
            <TextField
              label="Max Qty (empty = ∞)"
              type="number"
              size="small"
              value={tier.max_qty}
              onChange={(e) => updateTier(i, 'max_qty', e.target.value)}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Discount %"
              type="number"
              size="small"
              value={tier.discount_pct}
              onChange={(e) => updateTier(i, 'discount_pct', e.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              required
            />
            <IconButton
              size="small"
              onClick={() => removeTier(i)}
              disabled={tiers.length <= 1}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TrendingUpIcon sx={{ color: '#16a34a' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
          Profit Preview
        </Typography>
        <Chip label="Internal — not shown to customers" size="small" sx={{ ml: 1, fontSize: '10px', height: '18px', backgroundColor: '#fef3c7', color: '#92400e' }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Margin on cost depends only on the coefficient and discount %. Below, one column per active coefficient rule that could apply to products matching this rule's price filter.
      </Typography>

      {(() => {
        const active = (coefficientRules || []).filter(r => r.active == 1);
        if (active.length === 0) {
          return (
            <Alert severity="warning">
              No active coefficient rules — can't compute profit. Add one in <strong>Coefficient Rules</strong> first.
            </Alert>
          );
        }

        // Volume-discount min/max is matched against SELL price. Coef rules cover BASE
        // price ranges. We compute the SELL range each coef rule maps to and only
        // surface coefficient rules that overlap with this rule's sell range.
        const vdMinSell = formData.min_price === '' || formData.min_price == null ? -Infinity : parseFloat(formData.min_price);
        const vdMaxSell = formData.max_price === '' || formData.max_price == null ? Infinity  : parseFloat(formData.max_price);

        const applicable = active.filter(r => {
          const c = parseFloat(r.coefficient) || 1;
          const rMinSell = r.min_price != null ? parseFloat(r.min_price) * c : -Infinity;
          const rMaxSell = r.max_price != null ? parseFloat(r.max_price) * c : Infinity;
          return rMaxSell >= vdMinSell && rMinSell <= vdMaxSell;
        });

        const shown = applicable.length ? applicable : active;
        const cleanedTiers = tiers.filter(t => t.min_qty !== '' && t.discount_pct !== '');

        const cellColor = (margin) => {
          if (margin < 0)  return { color: '#dc2626', bg: '#fef2f2' };
          if (margin < 20) return { color: '#d97706', bg: '#fff7ed' };
          return { color: '#16a34a', bg: '#f0fdf4' };
        };

        return (
          <>
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#6b7280', minWidth: 110 }}>QTY</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#6b7280' }} align="right">DISCOUNT</TableCell>
                    {shown.map(r => {
                      const c = parseFloat(r.coefficient).toFixed(2);
                      return (
                        <TableCell key={r.id} sx={{ fontWeight: 600, fontSize: 11, color: '#6b7280', borderLeft: '1px solid #e5e7eb' }} align="right">
                          <Box>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#374151' }}>
                              {r.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#845adf', fontWeight: 600 }}>
                              × {c}
                            </Typography>
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cleanedTiers.map((tier, i) => {
                    const pct = parseFloat(tier.discount_pct) || 0;
                    return (
                      <TableRow key={i}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {tier.min_qty}{tier.max_qty ? `–${tier.max_qty}` : '+'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {pct > 0 ? (
                            <Chip label={`-${pct}%`} size="small" sx={{ height: '20px', fontSize: '10px', backgroundColor: '#fef3c7', color: '#92400e', fontWeight: 600 }} />
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        {shown.map(r => {
                          const c = parseFloat(r.coefficient) || 1;
                          // margin_pct = (coef × (1 - discount/100) - 1) × 100
                          const margin = (c * (1 - pct / 100) - 1) * 100;
                          const col = cellColor(margin);
                          return (
                            <TableCell key={r.id} align="right" sx={{ borderLeft: '1px solid #e5e7eb', backgroundColor: col.bg }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: col.color }}>
                                {margin >= 0 ? '+' : ''}{margin.toFixed(0)}%
                              </Typography>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {applicable.length === 0 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#d97706' }}>
                No coefficient rule overlaps this rule's price filter — showing all rules for reference.
              </Typography>
            )}

            {(() => {
              const lossy = cleanedTiers.some(tier => {
                const pct = parseFloat(tier.discount_pct) || 0;
                return shown.some(r => {
                  const c = parseFloat(r.coefficient) || 1;
                  return (c * (1 - pct / 100) - 1) * 100 < 0;
                });
              });
              if (!lossy) return null;
              return (
                <Alert severity="error" sx={{ mt: 2 }}>
                  One or more tiers go <strong>below cost</strong> with at least one of the coefficient rules above. You will lose money on those units.
                </Alert>
              );
            })()}
          </>
        );
      })()}
    </Box>
  );
};

export default VolumeDiscountForm;
